import { List, Map } from "immutable";
import { uniq } from "lodash";
import createCachedSelector from "re-reselect";

import { immutableRecords } from "../../../../types";
import { appStateActionTypes, appStateSelectors } from "../../../appState";
import { characterWidthMapActionTypes, characterWidthMapSelectors } from "../../../characterWidthMap";
import { definitionActionTypes } from "../../../definition";
import { colorActionTypes, colorSelectors } from "../../../definition/colors";
import { referenceSelectors } from "../../../definition/references";
import { typeSelectors } from "../../../definition/types";
import { itemSelectors } from "../../../items";
import { preferencesActionTypes } from "../../../preferences";
import rootActionTypes from "../../../rootActionTypes";
import displayCacheSelectors from "../../selectors";
import { getAffectedActionIds } from "./getIdsFromActions";

// Possible change paths:
//  -- User changes related item shortLabel or color
//  -- User adds or removes a related item to this item
//  -- Global color property is changed
//  -- Data is first loaded

const areRelevantItemValuesSame = (newItem, prevItem) =>
  newItem &&
  prevItem &&
  newItem.color === prevItem.color &&
  newItem.shortLabel === prevItem.shortLabel &&
  newItem.initials === prevItem.initials &&
  newItem.type === prevItem.type &&
  newItem.label === prevItem.label;

const areItemRelationshipsTheSame = (newItem, prevItem) =>
  newItem &&
  prevItem &&
  newItem.references === prevItem.references &&
  newItem.inverseReferences === prevItem.inverseReferences;

const globalActionTypes = [
  //#!if readonly !== true
// colorActionTypes.CHANGE_COLOR,
// colorActionTypes.DELETE_COLOR,
  //#!endif
  definitionActionTypes.REPLACE_DEFINITIONS,
  rootActionTypes.LOAD_FILE,
  rootActionTypes.UPDATE_FILE,
  rootActionTypes.LOAD_SAMPLE,
  appStateActionTypes.SET_FONT_PERCENTAGE,
  preferencesActionTypes.SET_GLOBAL_FONT_SIZE,
  characterWidthMapActionTypes.ADD_CHARACTER_WIDTHS
];

/**
 * @param {ItemRecord} item
 * @param {Map} relationshipsById
 * @param {Map} referencesById
 */
const getReferencedItemsForItem = createCachedSelector(
  // inputs
  (item, _relationshipsById) => item,
  (_item, relationshipsById) => relationshipsById,
  (_item, _relationshipsById, referencesById) => referencesById,
  // output
  (item, relationshipsById, referencesById) => {
    const refObjs = item.references.map(rId => relationshipsById.get(rId).object);
    // Includes the subject of the bi-direction references
    const refBiSubs = item.inverseReferences.reduce((result, rId) => {
      const rel = relationshipsById.get(rId);
      const ref = referencesById.get(rel.reference);
      return ref.bidirectional ? result.push(rel.subject) : result;
    }, List());
    return refObjs
      .concat(refBiSubs)
      .toSet()
      .toList();
  }
)(item => item.id);

/**
 * @param {ItemRecord} item
 * @param {Map} relationshipsById
 * @param {Map} itemsById
 * @param {Map} typesById
 * @param {List} typeAllIds
 * @param {Map} colorMap
 * @param {Map} globalItemIndexes
 * @param {Map} referencesById
 * @param {Map} fontSize
 * @param {Map} widthMap
 */
export const getRelatedItemsByType = createCachedSelector(
  // inputs
  (...params) => params[0], // item
  (...params) => params[1], // relationshipsById
  (...params) => params[2], // itemsById
  (...params) => params[3], // typesById
  (...params) => params[4], // typeAllIds
  (...params) => params[5], // colorMap
  (...params) => params[6], // globalItemIndexes
  (...params) => params[7], // referencesById
  (...params) => params[8], // fontSize
  (...params) => params[9], // widthMap
  // output
  (
    item,
    relationshipsById,
    itemsById,
    typesById,
    typeAllIds,
    colorMap,
    globalItemIndexes,
    referencesById,
    fontSize,
    widthMap
  ) => {
    const referencedItemIds = getReferencedItemsForItem(item, relationshipsById, referencesById);
    const sortByGlobalOrder = (a, b) => {
      return globalItemIndexes.get(a.id) - globalItemIndexes.get(b.id);
    };
    const relatedItems = referencedItemIds
      .reduce((idsByType, id) => {
        const item = itemsById.get(id);
        return item
          ? idsByType.update(item.type, idDisplayList => {
            const display = displayCacheSelectors.getIdentifierDisplayForItem(
              item,
              typesById,
              colorMap,
              fontSize,
              widthMap
            );
            return idDisplayList ? idDisplayList.push(display) : List([display]);
          })
          : idsByType;
      }, Map())
      .map(idDisplayList => idDisplayList.sort(sortByGlobalOrder));
    // Convert to list of {typeId, idDisplayList}
    return typeAllIds.reduce((list, typeId) => {
      const idDisplayList = relatedItems.get(typeId);
      return idDisplayList
        ? list.push(immutableRecords.IdentifierDisplayListOfTypeRecord({ typeId, idDisplayList }))
        : list;
    }, List());
  }
)(item => item.id);

const updateRelatedItemsByType = (
  state,
  action,
  newAndChangedIdSet,
  newItemsById,
  prevItemsById,
  newRelationshipsById,
  prevRelationshipsById,
  newGlobalState
) => {
  let { affectedIds, matchedGlobal } = getAffectedActionIds(state, action, globalActionTypes, []);

  if (!matchedGlobal) {
    // We only need to consider newAndChangedIdSet + any items that are related to those that changed
    const dataChangedIds = newAndChangedIdSet.filter(
      cId => !areRelevantItemValuesSame(newItemsById.get(cId), prevItemsById.get(cId))
    );

    const relationshipChangedIds = newAndChangedIdSet.filter(
      cId => !areItemRelationshipsTheSame(newItemsById.get(cId), prevItemsById.get(cId))
    );

    // We need to look at items related to those items
    const relatedIds = dataChangedIds.reduce((result, cId) => {
      const currItem = newItemsById.get(cId);
      const prevItem = prevItemsById.get(cId);

      const currRelatedIds = currItem.references.map(relId => newRelationshipsById.get(relId).object);
      const currInverseRelatedIds = currItem.inverseReferences.map(relId => newRelationshipsById.get(relId).subject);
      result.push(...currRelatedIds.toArray());
      result.push(...currInverseRelatedIds.toArray());
      if (prevItem) {
        const prevRelatedIds = prevItem.references.map(relId => prevRelationshipsById.get(relId).object);
        const prevInverseRelatedIds = prevItem.inverseReferences.map(relId => prevRelationshipsById.get(relId).subject);
        result.push(...prevRelatedIds.toArray());
        result.push(...prevInverseRelatedIds.toArray());
      }
      return result;
    }, []);

    affectedIds = uniq([...relationshipChangedIds.toArray(), ...relatedIds]);
  }

  if (affectedIds.length === 0) {
    return state;
  }

  const typesById = typeSelectors.getTypesById(newGlobalState);
  const typeAllIds = typeSelectors.getAllIds(newGlobalState);
  const colorsById = colorSelectors.getColorsById(newGlobalState);
  const globalItemIndexes = itemSelectors.getItemIndexesInTreeOrderForAllTypes(newGlobalState);
  const referencesById = referenceSelectors.getReferencesById(newGlobalState);
  const fontSize = appStateSelectors.getFontSize(newGlobalState);
  const widthMap = characterWidthMapSelectors.getCharacterWidthMap(newGlobalState);

  return state.withMutations(s => {
    affectedIds.forEach(cId => {
      const item = newItemsById.get(cId);
      const rec = state.get(cId);
      const relatedItemsByType = getRelatedItemsByType(
        item,
        newRelationshipsById,
        newItemsById,
        typesById,
        typeAllIds,
        colorsById,
        globalItemIndexes,
        referencesById,
        fontSize,
        widthMap
      );
      const oldString = JSON.stringify(rec.relatedItemsByType.toJS());
      const newString = JSON.stringify(relatedItemsByType.toJS());
      if (oldString !== newString) {
        s.update(cId, d => d.set("relatedItemsByType", relatedItemsByType));
      }
    });
  });
};

export default updateRelatedItemsByType;
